import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, FileText, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supervisorAlertService } from '../../services/supervisorAlertService';
import { realtimeNotificationService } from '../../services/realtimeNotificationService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import EscalatedAlertCard from './components/EscalatedAlertCard';
import ResponseTimeMetrics from './components/ResponseTimeMetrics';
import EnforcementPanel from './components/EnforcementPanel';

const SupervisorAlertManagementDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [escalatedAlerts, setEscalatedAlerts] = useState([]);
  const [teamMetrics, setTeamMetrics] = useState([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showEnforcementPanel, setShowEnforcementPanel] = useState(false);
  const [filters, setFilters] = useState({
    escalationLevel: null,
    severity: null
  });

  // Real-time subscription setup
  useEffect(() => {
    if (!user?.id) return;

    // Get user's company ID
    const setupRealtimeSubscriptions = async () => {
      try {
        const { data: employeeData } = await supabase
          ?.from('company_employees')
          ?.select('company_id')
          ?.eq('user_id', user?.id)
          ?.single();

        if (!employeeData?.company_id) return;

        const companyId = employeeData?.company_id;

        // Subscribe to compliance alerts
        realtimeNotificationService?.subscribeToComplianceAlerts(companyId, (payload) => {
          console.log('Real-time compliance alert update:', payload);
          // Reload dashboard data on any alert change
          loadDashboardData();
        });

        // Subscribe to environmental alerts
        realtimeNotificationService?.subscribeToEnvironmentalAlerts(user?.id, (payload) => {
          console.log('Real-time environmental alert update:', payload);
          loadDashboardData();
        });

        // Subscribe to escalation history
        realtimeNotificationService?.subscribeToEscalationHistory(companyId, (payload) => {
          console.log('Real-time escalation update:', payload);
          loadDashboardData();
        });

        // Subscribe to alert response tracking
        realtimeNotificationService?.subscribeToAlertResponseTracking(companyId, (payload) => {
          console.log('Real-time alert tracking update:', payload);
          loadDashboardData();
        });
      } catch (error) {
        console.error('Error setting up real-time subscriptions:', error);
      }
    };

    setupRealtimeSubscriptions();

    // Cleanup subscriptions on unmount
    return () => {
      realtimeNotificationService?.unsubscribeAll();
    };
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user?.id, filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [alerts, metrics, criticalCount] = await Promise.all([
        supervisorAlertService?.getEscalatedAlerts(user?.id, filters),
        supervisorAlertService?.getTeamResponseMetrics(user?.id),
        supervisorAlertService?.getUnacknowledgedCriticalCount(user?.id)
      ]);

      setEscalatedAlerts(alerts);
      setTeamMetrics(metrics);
      setUnacknowledgedCount(criticalCount);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev?.[filterType] === value ? null : value
    }));
  };

  const handleExportReport = async () => {
    try {
      await supervisorAlertService?.exportSupervisorReport(user?.id, {
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString(),
        endDate: new Date()?.toISOString()
      });
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  const handleAlertAction = (alert, action) => {
    setSelectedAlert(alert);
    setShowEnforcementPanel(true);
  };

  const avgResponseTime = teamMetrics?.length > 0
    ? Math.round(teamMetrics?.reduce((sum, m) => sum + m?.avgResponseTime, 0) / teamMetrics?.length)
    : 0;

  const criticalAlerts = escalatedAlerts?.filter(a => a?.severity === 'critical')?.length || 0;
  const overdueAlerts = escalatedAlerts?.filter(a => a?.escalationLevel === 'overdue')?.length || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Supervisor Alert Management</h1>
            <p className="text-gray-600 mt-1">Monitor escalated alerts and team response performance</p>
          </div>
          <Button onClick={handleExportReport} className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Export Report
          </Button>
        </div>

        {/* Critical Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unacknowledged Critical</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{unacknowledgedCount}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Response Time</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{avgResponseTime}m</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Critical Alerts</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{criticalAlerts}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue Responses</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{overdueAlerts}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <XCircle className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Alert Priority Sidebar */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Escalation Filters</h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Escalation Level</p>
                  <div className="space-y-2">
                    {[
                      { value: 'unacknowledged_critical', label: 'Unacknowledged Critical', color: 'red' },
                      { value: 'overdue_response', label: 'Overdue Response', color: 'orange' },
                      { value: 'team_escalated', label: 'Team Escalated', color: 'yellow' }
                    ]?.map(level => (
                      <button
                        key={level?.value}
                        onClick={() => handleFilterChange('escalationLevel', level?.value)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          filters?.escalationLevel === level?.value
                            ? `bg-${level?.color}-100 text-${level?.color}-700 border border-${level?.color}-300`
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {level?.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Severity</p>
                  <div className="space-y-2">
                    {[
                      { value: 'critical', label: 'Critical', color: 'red' },
                      { value: 'high', label: 'High', color: 'orange' }
                    ]?.map(severity => (
                      <button
                        key={severity?.value}
                        onClick={() => handleFilterChange('severity', severity?.value)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          filters?.severity === severity?.value
                            ? `bg-${severity?.color}-100 text-${severity?.color}-700 border border-${severity?.color}-300`
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {severity?.label}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setFilters({ escalationLevel: null, severity: null })}
                  className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Response Time Thresholds */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mt-4">
              <h3 className="font-semibold text-gray-900 mb-3">Response Thresholds</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Critical:</span>
                  <span className="font-medium text-red-600">1 hour</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">High:</span>
                  <span className="font-medium text-orange-600">2 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Standard:</span>
                  <span className="font-medium text-yellow-600">4 hours</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Dashboard Area */}
          <div className="lg:col-span-9 space-y-6">
            {/* Real-time Escalated Alert Feed */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Escalated Alerts</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {escalatedAlerts?.length} alert{escalatedAlerts?.length !== 1 ? 's' : ''} requiring attention
                </p>
              </div>

              <div className="divide-y divide-gray-200">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Loading alerts...</div>
                ) : escalatedAlerts?.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p>No escalated alerts at this time</p>
                  </div>
                ) : (
                  escalatedAlerts?.map(alert => (
                    <EscalatedAlertCard
                      key={alert?.id}
                      alert={alert}
                      onAction={handleAlertAction}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Team Response Time Metrics */}
            <ResponseTimeMetrics metrics={teamMetrics} loading={loading} />
          </div>
        </div>
      </div>

      {/* Enforcement Panel Modal */}
      {showEnforcementPanel && selectedAlert && (
        <EnforcementPanel
          alert={selectedAlert}
          supervisorId={user?.id}
          onClose={() => {
            setShowEnforcementPanel(false);
            setSelectedAlert(null);
            loadDashboardData();
          }}
        />
      )}
    </div>
  );
};

export default SupervisorAlertManagementDashboard;